<template>
  <div class="bidders-table table-component">
    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :search="tableData.search" :fields="tableFields" selectable
                   show-toggle-columns
                   :enable-simple-search="false" export-vuex-module-name="bidders" :export-attributes="exportAttributes"
                   export-permission-name="export_auction" @paramsChanged="getTableData" :extra-data="extraData"
                   filter-toggle-string="bidders-table" :enable-frontend-export="enableFrontendExport"
                   @update-toggle-columns="updateToggleColumns" @on-column-save="updateToggleColumns">
      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown class="btn-table-actions" :text="$t('Actions')" variant="primary"
                    v-permission="changePermission">
          <b-dropdown-item v-if="!hideAction(ACTION_CREATE_NEW_BIDDER)"
                           v-permission="changePermission"
                           :disabled="tableData.loading" @click="onNewBidderClick">
            {{ $t("New Bidder") }}
          </b-dropdown-item>
          <b-dropdown-item @click="onPaddleNumbersPrint(selectedItems)"
                           :disabled="paddleNumbersPrintLoading || !selectedItems.size">
            <b-spinner style="width: 1rem; height: 1rem" class="mr-2" v-if="paddleNumbersPrintLoading"/>
            {{ $t("Print Paddle Numbers") }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="bidders-table">
          <bidder-search :update-search-model="updateSearchModel" :get-table-data="getTableData"
                         :vuex-search-model="tableData.searchModel" :exclude-search-fields="excludeSearchFields"/>
        </search-collapse>
      </template>
      <template slot="cell(customer_uuid)" slot-scope="{index, item: bidder}">
        <router-link v-if="bidder.customer && $hasPermission('view_customer')"
                     :to="{ name: 'customers.dashboard', params: { id: bidder.customer_uuid }}">
          {{ bidder.customer.display_name }}
        </router-link>
        <p class="m-0" v-else-if="bidder.customer"> {{ bidder.customer.display_name }} </p>

        <router-link v-if="bidder.customer.term && bidder.customer.term.red_flagged" :to="{name: 'customers.terms', params: {id: bidder.customer.uuid}}">
          <b-tooltip :target="`red-flag-${index}`">
            <template v-slot:default>
              <span class="red-flag-comment" v-html="bidder.customer.term.red_flag_comment"/>
            </template>
          </b-tooltip>
          <font-awesome-icon icon="flag" class="icon text-danger mr-3 red-flag-icon ml-2" :id="`red-flag-${index}`"/>
        </router-link>
        <action-buttons
          :edit-permission="changePermission"
          :delete-permission="deletePermission"
          :view="false"
          :edit="!hideAction(ACTION_EDIT_BIDDER)"
          :remove="!hideAction(ACTION_REMOVE_BIDDER)"
          @editClick="onEditClick(bidder)"
          @deleteClick="onRemoveClick(bidder)"/>
      </template>

      <template slot="cell(shipping_address_1)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.shipping_address">
          {{ bidder.customer.shipping_address.address_1 }}
        </div>
      </template>
      <template slot="cell(shipping_address_2)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.shipping_address">
          {{ bidder.customer.shipping_address.address_2 }}
        </div>
      </template>
      <template slot="cell(shipping_state)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.shipping_address">
          {{ bidder.customer.shipping_address.state }}
        </div>
      </template>
      <template slot="cell(shipping_county)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.shipping_address">
          {{ bidder.customer.shipping_address.county }}
        </div>
      </template>
      <template slot="cell(shipping_city)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.shipping_address">
          {{ bidder.customer.shipping_address.city }}
        </div>
      </template>
      <template slot="cell(shipping_zip_code)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.shipping_address">
          {{ bidder.customer.shipping_address.zip_code }}
        </div>
      </template>
      <template slot="cell(shipping_country)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.shipping_address && bidder.customer.shipping_address.country">
          {{ bidder.customer.shipping_address.country.name }}
        </div>
      </template>
      <template slot="cell(billing_address_1)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.billing_address">
          {{ bidder.customer.billing_address.address_1 }}
        </div>
      </template>
      <template slot="cell(billing_address_2)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.billing_address">
          {{ bidder.customer.billing_address.address_2 }}
        </div>
      </template>
      <template slot="cell(billing_state)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.billing_address">
          {{ bidder.customer.billing_address.state }}
        </div>
      </template>
      <template slot="cell(billing_county)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.billing_address">
          {{ bidder.customer.billing_address.county }}
        </div>
      </template>
      <template slot="cell(billing_city)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.billing_address">
          {{ bidder.customer.billing_address.city }}
        </div>
      </template>
      <template slot="cell(billing_zip_code)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.billing_address">
          {{ bidder.customer.billing_address.zip_code }}
        </div>
      </template>
      <template slot="cell(billing_country)" slot-scope="{item: bidder}">
        <div v-if="bidder.customer && bidder.customer.billing_address && bidder.customer.billing_address.country">
          {{ bidder.customer.billing_address.country.name }}
        </div>
      </template>


      <template slot="cell(customer_email)" slot-scope="{item: bidder}">
        <span v-if="bidder.customer">
          {{ bidder.customer.email }}
        </span>
      </template>
      <template slot="cell(customer_primary_phone)" slot-scope="{item: bidder}">
        <span v-if="bidder.customer">
          {{ bidder.customer.primary_phone }}
        </span>
      </template>
      <template slot="cell(preferred_clerk)" slot-scope="{item: bidder}">
        {{ bidder.preferred_clerk ? bidder.preferred_clerk.name : "" }}
      </template>
      <template slot="cell(language)" slot-scope="{item: bidder}">
        {{ bidder.language ? bidder.language.name : "" }}
      </template>
      <template slot="cell(sale_no)" slot-scope="{item: bidder}">
        {{ bidder.auction.sale_no }}
      </template>
      <template slot="cell(date)" slot-scope="{item: bidder}">
        {{ bidder.date | toDateTime }}
      </template>
      <template slot="cell(total_spent)" slot-scope="{item: bidder}">
        {{ bidder.total_spent | asCurrency(bidder.auction.currency.symbol) }}
      </template>
      <template slot="cell(company_name)" slot-scope="{item: bidder}">
        <span v-if="bidder.customer">
          {{ bidder.customer.company_name }}
        </span>
      </template>
      <template slot="cell(client_tier)" slot-scope="{item: bidder}">
          <client-tier-display-widget :customer="bidder.customer" />
      </template>
      <template slot="cell(is_company)" slot-scope="{item: bidder}">
        <span  v-html="$options.filters.asBadgeBoolean(bidder.customer.is_company)"/>
      </template>
      <template slot="cell(status)" slot-scope="{item: bidder}">
        <span v-as-status="bidder.status" v-if="bidder.status">
          {{ bidder.status | removeUnderscores | toUpperCaseEveryWord }}
        </span>
      </template>

      <template slot="cell(personal_identification_number)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ bidder.customer.identification.personal_identification_number }}
        </template>
      </template>

      <template slot="cell(document_type)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ customerIdentificationTypesMap[bidder.customer.identification.document_type] }}
        </template>
      </template>

      <template slot="cell(document_number)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ bidder.customer.identification.document_number }}
        </template>
      </template>

      <template slot="cell(document_issue_date)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ bidder.customer.identification.document_issue_date }}
        </template>
      </template>

      <template slot="cell(document_expiry_date)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ bidder.customer.identification.document_expiry_date }}
        </template>
      </template>

      <template slot="cell(date_of_birth)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ bidder.customer.identification.date_of_birth }}
        </template>
      </template>

      <template slot="cell(citizenship_code)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ countryMap[bidder.customer.identification.citizenship_code] }}
        </template>
      </template>

      <template slot="cell(residency_code)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ countryMap[bidder.customer.identification.residency_code] }}
        </template>
      </template>

      <template slot="cell(country_of_birth_code)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ countryMap[bidder.customer.identification.country_of_birth_code] }}
        </template>
      </template>

      <template slot="cell(place_of_issue)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ bidder.customer.identification.place_of_issue }}
        </template>
      </template>

      <template slot="cell(company_reg_number)" slot-scope="{item: bidder}">
        <template v-if="bidder.customer.identification">
          {{ bidder.customer.identification.company_reg_number }}
        </template>
      </template>

      <template slot="cell(trust_level)" slot-scope="{item: bidder}">
        <template v-if="bidder.credit_limit">
          {{ getTrustLevelByBidder(bidder.credit_limit) }}
        </template>
      </template>

      <template slot="cell(credit_limit)" slot-scope="{item: bidder}">
        <template v-if="bidder.credit_limit">
          {{ bidder.credit_limit | asCurrency(bidder.auction.currency.symbol) }}
        </template>
      </template>

      <template slot="cell(total_purchases)" slot-scope="{item: bidder}">
        {{ bidder.total_purchases | asCurrency(bidder.auction.currency.symbol) }}
      </template>

      <template slot="cell(total_commission_bids)" slot-scope="{item: bidder}">
        {{ bidder.total_commission_bids ? bidder.total_commission_bids : '' }}
      </template>

      <template slot="cell(total_telephone_bids)" slot-scope="{item: bidder}">
        {{ bidder.total_telephone_bids ? bidder.total_telephone_bids : '' }}
      </template>

      <template slot="cell(identity_verification)" slot-scope="{item: bidder}">
        {{bidder.customer.term ? bidder.customer.term.identity_verification : '' | toUpperCaseFirst }}
      </template>

      <template slot="cell(kyc_status_verification)" slot-scope="{item: bidder}">
        {{bidder.customer.term ? bidder.customer.term.kyc_status_verification : '' | toUpperCaseFirst }}
      </template>

      <template slot="cell(watchlist)" slot-scope="{item: bidder}">
        <span v-if="!bidder.watchlist">
          [{{ bidder.watchlist }}] Lots
        </span>
        <router-link
          :to="{ name: 'customers.favoriteLots', params: { id: bidder.customer_uuid, sale_no: bidder.auction.sale_no }}"
          v-else>
          [{{ bidder.watchlist }}] Lots
        </router-link>
      </template>

      <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                v-for="dynamicField in toggleColumns.dynamicFields">
        <span v-if="item.customer.identification && item.customer.identification.dynamic_fields && item.customer.identification.dynamic_fields[dynamicField]"
          v-html="$options.filters.asDynamicField(item.customer.identification.dynamic_fields[dynamicField], field.type, field.choices)">
        </span>
      </template>

      <template slot="cell(actions)" slot-scope="{item: bidder}">
        <div class="position-relative">
          <b-dropdown right class="text-white" variant="primary" no-caret size="sm">
            <template #button-content>
              <font-awesome-icon icon="ellipsis-v" class="icon"/>
            </template>
            <table-notes-button :item="bidder"
                                :source="SOURCE_BIDDERS_TABLE" object-name="bidder" is-dd-item
                                plural-object-name="bidders" change-permission="change_auction"
                                delete-permission="change_auction" :hide-footer="!$hasPermission('change_auction')"/>
          </b-dropdown>
          <b-badge v-if="bidder && bidder.total_notes" class="total-notes">
            {{ bidder.total_notes }}
          </b-badge>
        </div>
      </template>
    </artisio-table>
  </div>
</template>

<script>
  import i18nHelper from "./../../core/services/i18n.service";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {
    ACTION_CREATE_NEW_BIDDER,
    ACTION_EDIT_BIDDER,
    ACTION_REMOVE_BIDDER,
    BIDDERS_TABLE_NAME,
    SOURCE_BIDDERS_TABLE, WS_PADDLE_NUMBER_PRINT
  } from "../constants";
  import i18n from "../../shared/i18n";
  import ActionButtons from "../../core/components/ams-table/ActionButtons";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import BidderSearch from "./BidderSearch";
  import TableNotesButton from "@/modules/notes/TableNotesButton";
  import uuidV4 from 'uuid';
  import _ from "lodash";
  import ClientTierDisplayWidget from "@/core/widgets/ClientTierDisplayWidget.vue";

  const {mapActions, mapState} = createNamespacedHelpers('bidders');
  const {mapState: mapStateGlobal, mapGetters: globalMapGetters} = createNamespacedHelpers('global');

  export default {
    name: "BiddersTable",
    components: {ClientTierDisplayWidget, TableNotesButton, BidderSearch, SearchCollapse, ActionButtons, ArtisioTable},
    props: {
      auction: {
        type: Object,
      },
      changePermission: {
        type: String,
        default: ''
      },
      deletePermission: {
        type: String,
        default: ''
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
      hideActions: {
        type: [Boolean, Array],
        default: () => []
      },
      enableExport: {
        type: Boolean,
        default: true
      },
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
    },
    data: () => {
      return {
        masterLanguage: i18nHelper.getMasterLanguage(),
        exportAttributes: {
          "customer.display_name": i18n.t('Customer'),
          "customer.email": i18n.t('Email'),
          "customer.primary_phone": i18n.t('Phone'),
          paddle_number: i18n.t('Paddle Number'),
          "customer.company_name": i18n.t('Company Name'),
          "bidder.preferred_clerk.name": i18n.t('Preferred Clerk'),
          "bidder.language.name": i18n.t('Language'),
          date: i18n.t('Registration Date'),
          status: i18n.t('Status'),
        },
        ACTION_CREATE_NEW_BIDDER: ACTION_CREATE_NEW_BIDDER,
        ACTION_EDIT_BIDDER: ACTION_EDIT_BIDDER,
        ACTION_REMOVE_BIDDER: ACTION_REMOVE_BIDDER,
        SOURCE_BIDDERS_TABLE: SOURCE_BIDDERS_TABLE,
        paddleNumbersPrintLoading: false
      }
    },
    computed: {
      ...mapState({
        tableSource: state => state.tableSource,
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
      }),
      ...mapStateGlobal({
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
        enableContinuousAuction: state => state.enableContinuousAuction,
        customerIdentificationTypes: state => state.customerIdentificationTypes,
        enableTrustLevels: state => state.currentBranch.enable_trust_levels,
      }),
      ...globalMapGetters(['countryMap', 'trustLevels']),
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.excludeColumns,
          tableName: `${BIDDERS_TABLE_NAME}.${this.tableSource}`,
        }
      },
      bidderCreationRestricted() {
        return !this.hasArtisioTimedProvider && this.auction.type === "timed";
      },
      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields)
          .filter(field => !this.excludeColumns.includes(field.key) && field.isVisible)
          .map(field => {
            if (field.key === 'sale_no') {
              field.label = this.enableContinuousAuction ? this.$t('Lot') : this.$t('Sale')
            }
            return field;
          })
      },
      customerIdentificationTypesMap: state => {
        return Object.fromEntries(state.customerIdentificationTypes.map(c => ([c.value, c.key])))
      },
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'removeBidder', 'updateSearchModel', 'batchPrintPaddleNumbers', 'updateToggleColumns']),

      getTrustLevelByBidder(limit) {
        const item = this.trustLevels.find(tr => tr.credit_limit === limit);
        return item ? item.name : ''
      },
      onNewBidderClick() {
        this.showModal({
          object: {
            auction: this.auction,
            auction_uuid: this.auction.uuid
          },
          source: SOURCE_BIDDERS_TABLE
        })
      },
      async onPaddleNumbersPrint(items) {
        this.paddleNumbersPrintLoading = true;
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));
        let response = await this.batchPrintPaddleNumbers({uuids: uuids})
        if (!response.success && response.body && response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        } else {
          this.$toast('success', this.$i18n.t("PDF generation for paddle numbers has successfully started."));
        }
      },
      onPaddleNumberPrintFinished(data) {
        this.paddleNumbersPrintLoading = false;
        if (data.errors) {
          if (data.errors.non_field_errors) {
            this.$errorDialog(data.errors.non_field_errors)
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong. Please, try again later"))
          }
          return;
        }
        const id = uuidV4();
        const title = this.$i18n.t(`Paddle Numbers Result`);
        let message = this.$i18n.t('Paddle Numbers Output is Ready. Click to download.');
        const h = this.$createElement;
        const vNodesMsg = h('p', {class: ['mb-0']}, [message])

        const buttons = [];
        if (data.pdf) {
          buttons.push(h(
            'b-button',
            {
              props: {variant: 'success',},
              on: {
                click: () => {
                  if (!data.xml) {
                    this.$bvToast.hide(id);
                  }
                  window.open(data.pdf, '_blank')
                }
              }
            },
            i18n.t('PDF')
          ));
        }
        buttons.push(h(
          'b-button',
          {
            class: ['ml-2'],
            props: {variant: 'secondary',},
            on: {
              click: () => {
                this.$bvToast.hide(id);
              }
            }
          },
          i18n.t('Dismiss')
        ));

        const buttonsWrapper = h('div', {class: ['mt-2']}, buttons);
        this.$bvToast.toast([vNodesMsg, buttonsWrapper], {
          id: id,
          title: title,
          variant: 'info',
          toaster: 'b-toaster-top-center',
          noAutoHide: true
        })
      },
      async onEditClick(item) {
        this.showModal({object: item, source: SOURCE_BIDDERS_TABLE});
      },
      async onRemoveClick(item) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following bidder?"),
          this.$i18n.t('Bidder was successfully deleted'),
          this.removeBidder,
          item
        );
      },
      hideAction(actionName) {
        return this.hideActions.includes(actionName)
      },
    },
    mounted() {
      this.getTableData();
      this.$ws.listen(`bidders.${WS_PADDLE_NUMBER_PRINT}`, this.onPaddleNumberPrintFinished);
    },
    beforeDestroy() {
      this.$ws.remove(`bidders.${WS_PADDLE_NUMBER_PRINT}`);
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .total-notes {
    top: -7px;
    right: 12px;
    margin: 0;
    border-radius: 100%;
    padding: 3px 6px;
    font-size: 10px;
    position: absolute;
    background-color: #1ec973;
    color: white;
  }

  .artisio-table {
    /deep/ {
      .customer-col {
        min-width: 130px;
      }

      .sale-no-col {
        min-width: 130px;
      }

      .paddle-number-col {
        min-width: 140px;
      }

      .address-col {
        min-width: 200px;
      }

      .preferred-clerk-col {
        min-width: 120px;
      }

      .language-col {
        min-width: 90px;
      }

      .contact-number-col {
        min-width: 120px;
      }

      .total-spent-col {
        min-width: 130px;
      }

      .date-col {
        min-width: 150px;
      }

      .status-col {
        min-width: 90px;
      }

      .actions-col {
        width: 70px;
      }
    }
  }

  .red-flag-icon {
    cursor: pointer;
    outline: none !important;
  }

  .red-flag-comment {
    /deep/ > p {
      margin: 0;
    }
  }
</style>
